import { useWidgetChat } from "@/hooks/useWidgetChat";
import { ChatThread } from "@/components/chat/ChatThread";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { CallOverlay } from "@/components/chat/CallOverlay";
import { PreChatForm } from "@/components/widget/PreChatForm";
import { DOODLE_BG } from "@/lib/brand";
import { mediaUrl } from "@/lib/api";
import { initials } from "@/lib/format";
import { ArrowLeft, Phone, MoreVertical, MessageCircle, Loader2, RefreshCw } from "lucide-react";

export function StandaloneWidget({ apiKey, identity = null }) {
  const chat = useWidgetChat(apiKey, { autoStart: true, identity });
  const { config, session, messages, agentTyping, loading, starting, connected, accent, call, startSession } = chat;
  const identified = !!(identity && (identity.userId || identity.name || identity.email || identity.phone || identity.location));

  if (loading || (identified && !session)) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#0e8f7e]" aria-hidden="true" />
      </div>
    );
  }

  if (config?.error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-white p-6 text-center">
        <MessageCircle className="h-8 w-8 text-[#0e8f7e]" aria-hidden="true" />
        <p className="text-sm text-[#54656f]">This chat is unavailable.</p>
      </div>
    );
  }

  // Session couldn't be created (e.g. network dropped). Offer a retry instead
  // of spinning forever.
  if (!session) {
    if (config?.pre_chat?.enabled) {
      return (
        <div className="flex h-full flex-col bg-white">
          <div className="flex flex-shrink-0 items-center gap-2.5 border-b border-[#ededed] bg-white px-4 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[#e6dccb] text-base font-semibold text-[#7a6a4f]">
              {config.widget_avatar ? <img src={mediaUrl(config.widget_avatar)} alt="" className="h-full w-full object-cover" /> : initials(config.display_name)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-[16px] font-semibold text-[#111b18]">{config.display_name}</div>
              <div className="truncate text-[12px] text-[#667781]">{config.tagline}</div>
            </div>
          </div>
          <PreChatForm config={config} accent={accent} starting={starting} onStart={(f) => startSession(null, f)} />
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-white p-6 text-center" data-testid="widget-retry-screen">
        <MessageCircle className="h-9 w-9 text-[#0e8f7e]" aria-hidden="true" />
        <p className="max-w-[240px] text-sm text-[#54656f]">We couldn't connect you to the chat. Please check your connection.</p>
        <button
          onClick={() => startSession(null, "")}
          disabled={starting}
          className="flex items-center gap-2 rounded-full bg-[#0e8f7e] px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95 disabled:opacity-60"
          data-testid="widget-retry-button"
        >
          {starting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
          Try again
        </button>
      </div>
    );
  }

  const name = config.display_name;

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-white" style={{ overscrollBehavior: "contain" }}>
      {/* Header */}
      <div
        className="flex flex-shrink-0 items-center gap-1 border-b border-[#ededed] bg-white px-1.5 py-2"
        style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
      >
        <button className="p-2 text-[#111b18]" data-testid="widget-back-button" aria-label="Back">
          <ArrowLeft className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
        </button>
        <div className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#e6dccb] text-[19px] font-semibold text-[#7a6a4f]">
          {config.widget_avatar ? <img src={mediaUrl(config.widget_avatar)} alt="" className="h-full w-full object-cover" /> : initials(name)}
        </div>
        <div className="min-w-0 flex-1 pl-1.5">
          <div className="truncate text-[18px] font-semibold leading-tight text-[#111b18]">{name}</div>
          <div className="truncate text-[12px] text-[#667781]">{agentTyping ? <span className="text-[#0e8f7e]">typing…</span> : connected ? "online" : "connecting…"}</div>
        </div>
        {config.calls_enabled !== false && (
          <button onClick={call.startCall} className="p-2 text-[#111b18] transition-transform active:scale-90" data-testid="widget-start-call-button" aria-label="Voice call">
            <Phone className="h-[23px] w-[23px]" strokeWidth={2} aria-hidden="true" />
          </button>
        )}
        <button className="p-2 text-[#111b18]" data-testid="widget-menu-button" aria-label="Menu">
          <MoreVertical className="h-[22px] w-[22px]" aria-hidden="true" />
        </button>
      </div>

      <ChatThread
        messages={messages}
        isMine={(m) => m.sender_type === "visitor"}
        typingLabel={agentTyping ? "typing" : null}
        wallpaper={DOODLE_BG}
        translationActive={!!config.translation_enabled}
      />

      <div
        className="flex-shrink-0"
        style={{
          backgroundColor: "#e1d9ce",
          backgroundImage: `url(${DOODLE_BG})`,
          backgroundSize: "300px auto",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <ChatComposer onSend={chat.sendMessage} onTyping={chat.sendTyping} accent={accent} variant="whatsapp" />
      </div>

      <CallOverlay call={call} label={name} accent={accent} />
    </div>
  );
}
